import { Fragment, useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Button, Container, Input, Modal, ModalHeader, ModalBody, ModalFooter, Label } from "reactstrap";
import { generateApiKey, getApiKeys, revokeApiKey, regenerateApiKey, fetchBranches } from "../../utils/apiCalls";
import { errorToast } from "../../shared/Toast";
import { C, tbl, reportingGlobalCSS } from "../reporting/reportingStyles";
import { RCard, Badge, Loader } from "../reporting/ReportingShared";
import Breadcrumb from "../common/breadcrumb";

const ApiKeyList = () => {
  const userType = localStorage.getItem("userType");
  const merchantId = localStorage.getItem("MerchantId");
  const { selectedMerchant: reduxSelectedMerchant } = useSelector((state) => state.merchant || {});
  const selectedMerchant = userType === "merchant" ? merchantId : reduxSelectedMerchant;

  const [keys, setKeys] = useState([]);
  const [branches, setBranches] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [generateModal, setGenerateModal] = useState(false);
  const [newKeyResult, setNewKeyResult] = useState(null);
  const [generateForm, setGenerateForm] = useState({ appType: "pos", branchId: "", apiKey: "" });

  const fetchKeys = useCallback(async () => {
    if (!selectedMerchant || selectedMerchant === "all") { setKeys([]); return; }
    setIsLoading(true);
    const data = await getApiKeys(selectedMerchant);
    setKeys(data || []);
    setIsLoading(false);
  }, [selectedMerchant]);

  useEffect(() => { fetchKeys(); }, [fetchKeys]);

  useEffect(() => {
    const load = async () => {
      const response = await fetchBranches({}, false);
      let list = response?.data || [];
      if (selectedMerchant && selectedMerchant !== "all") list = list.filter((b) => String(b.MerchantId) === String(selectedMerchant));
      setBranches(list);
    };
    load();
  }, [selectedMerchant]);

  const handleGenerate = async () => {
    if (!selectedMerchant || selectedMerchant === "all") { errorToast("Please select a merchant first"); return; }
    if (generateForm.appType === "koinz" && !generateForm.apiKey.trim()) {
      errorToast("API key is required for Koinz — paste the key provided by Koinz");
      return;
    }
    const payload = { merchantId: Number(selectedMerchant), appType: generateForm.appType };
    if (generateForm.branchId) payload.branchId = Number(generateForm.branchId);
    if (generateForm.appType === "koinz") payload.apiKey = generateForm.apiKey.trim();
    const result = await generateApiKey(payload);
    if (result) { setNewKeyResult(result); setGenerateModal(false); fetchKeys(); }
  };

  const handleRevoke = async (keyId) => {
    if (!window.confirm("Revoke this API key?")) return;
    const result = await revokeApiKey(keyId);
    if (result) fetchKeys();
  };

  const handleRegenerate = async (keyId) => {
    if (!window.confirm("Revoke current key and generate a new one?")) return;
    const result = await regenerateApiKey(keyId);
    if (result) { setNewKeyResult(result); fetchKeys(); }
  };

  const appTypeBadge = (t) => {
    if (t === "pos") return { color: C.blue, bg: C.blueLight };
    if (t === "menu") return { color: C.green, bg: C.greenLight };
    if (t === "koinz") return { color: C.coral, bg: C.coralLight };
    if (t === "blend") return { color: C.purple, bg: C.purpleLight };
    return { color: C.orange, bg: C.orangeLight };
  };

  return (
    <Fragment>
      <style>{reportingGlobalCSS}</style>
      <Breadcrumb title="API Keys" parent="API Keys" />
      <Container fluid className="rpt-wrap">
        {/* Header */}
        <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 20 }}>
          {(!selectedMerchant || selectedMerchant === "all") && (
            <span style={{ color: C.orange, fontWeight: 600, fontSize: 13 }}>Please select a merchant from the dropdown</span>
          )}
          <div style={{ flex: 1 }} />
          <Button size="sm" style={{ background: `linear-gradient(135deg, ${C.coral}, #00bfa5)`, border: "none", borderRadius: 8, fontWeight: 600, fontSize: 12, padding: "8px 16px" }}
            onClick={() => { setGenerateForm({ appType: "pos", branchId: "", apiKey: "" }); setGenerateModal(true); }}>
            + Generate New Key
          </Button>
        </div>

        {/* New Key Banner */}
        {newKeyResult && (
          <RCard style={{ borderLeft: `4px solid ${C.green}`, marginBottom: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <div style={{ fontWeight: 700, color: C.text, marginBottom: 6 }}>New API Key Generated — Copy it now!</div>
                <code style={{ fontSize: 15, background: C.bg, padding: "8px 14px", borderRadius: 8, display: "inline-block", fontWeight: 600, border: `1px solid ${C.borderLight}` }}>{newKeyResult.apiKey}</code>
                <div style={{ fontSize: 12, color: C.textLight, marginTop: 6 }}>
                  App Type: <Badge color={appTypeBadge(newKeyResult.appType).color} bg={appTypeBadge(newKeyResult.appType).bg}>{newKeyResult.appType}</Badge>
                </div>
              </div>
              <Button size="sm" style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 8, color: C.textMed, fontWeight: 600, fontSize: 12 }}
                onClick={async () => {
                  try { await navigator.clipboard.writeText(newKeyResult.apiKey); setNewKeyResult(null); }
                  catch { errorToast("Failed to copy — please copy manually before dismissing"); }
                }}>
                Copy & Dismiss
              </Button>
            </div>
          </RCard>
        )}

        {/* Keys Table */}
        <RCard style={{ padding: 0, overflow: "hidden" }}>
          {isLoading ? <Loader /> : keys.length === 0 ? (
            <p style={{ textAlign: "center", padding: 40, color: C.textLight }}>
              {(!selectedMerchant || selectedMerchant === "all") ? "Select a merchant" : "No API keys found"}
            </p>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={tbl.table}>
                <thead>
                  <tr>
                    {["ID", "Key", "App Type", "Branch", "Status", "Last Used", "Created", "Actions"].map((h) => (
                      <th key={h} style={{ ...tbl.th, ...(h === "Actions" && { textAlign: "right" }) }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {keys.map((k) => {
                    const atb = appTypeBadge(k.appType);
                    return (
                      <tr key={k.id} className="rpt-row">
                        <td style={tbl.td}>{k.id}</td>
                        <td style={tbl.td}>
                          <code style={{ fontSize: 12, background: C.bg, padding: "2px 8px", borderRadius: 4, border: `1px solid ${C.borderLight}` }}>{k.apiKey}</code>
                        </td>
                        <td style={tbl.td}><Badge color={atb.color} bg={atb.bg}>{k.appType}</Badge></td>
                        <td style={tbl.td}>{k.Branch?.name || "All Branches"}</td>
                        <td style={tbl.td}>
                          <Badge color={k.active ? C.green : C.coral} bg={k.active ? C.greenLight : C.coralLight}>
                            {k.active ? "Active" : "Revoked"}
                          </Badge>
                        </td>
                        <td style={tbl.td}>{k.lastUsedAt ? new Date(k.lastUsedAt).toLocaleString() : <span style={{ color: C.textLight }}>Never</span>}</td>
                        <td style={tbl.td}>{new Date(k.createdAt).toLocaleDateString()}</td>
                        <td style={{ ...tbl.td, textAlign: "right" }}>
                          {k.active && (
                            <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                              {k.appType !== "koinz" && (
                                <Button size="sm" style={{ background: `linear-gradient(135deg, ${C.orange}, #FFD200)`, border: "none", borderRadius: 6, fontSize: 11, fontWeight: 700, color: "#fff" }} onClick={() => handleRegenerate(k.id)}>Regenerate</Button>
                              )}
                              <Button size="sm" style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 6, color: C.coral, fontSize: 11, fontWeight: 700 }} onClick={() => handleRevoke(k.id)}>Revoke</Button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </RCard>
      </Container>

      {/* Generate Modal */}
      <Modal isOpen={generateModal} toggle={() => setGenerateModal(false)}>
        <ModalHeader toggle={() => setGenerateModal(false)}>Generate New API Key</ModalHeader>
        <ModalBody>
          <Label>App Type *</Label>
          <Input type="select" value={generateForm.appType} onChange={(e) => setGenerateForm((prev) => ({ ...prev, appType: e.target.value }))} style={{ borderRadius: 8, border: `1px solid ${C.border}`, marginBottom: 12 }}>
            <option value="pos">POS</option>
            <option value="pickup">Pickup</option>
            <option value="menu">Menu</option>
            <option value="kds">KDS Screen</option>
            <option value="koinz">Koinz</option>
            <option value="blend">Blend</option>
          </Input>
          {generateForm.appType === "koinz" && (
            <>
              <Label>API Key * <span style={{ color: C.textLight, fontWeight: 400 }}>(provided by Koinz)</span></Label>
              <Input type="text" value={generateForm.apiKey} onChange={(e) => setGenerateForm((prev) => ({ ...prev, apiKey: e.target.value }))} placeholder="Paste the API key Koinz provided" style={{ borderRadius: 8, border: `1px solid ${C.border}`, marginBottom: 12, fontFamily: "monospace", fontSize: 13 }} />
            </>
          )}
          <Label>Branch (optional)</Label>
          <Input type="select" value={generateForm.branchId} onChange={(e) => setGenerateForm((prev) => ({ ...prev, branchId: e.target.value }))} style={{ borderRadius: 8, border: `1px solid ${C.border}` }}>
            <option value="">All Branches</option>
            {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </Input>
        </ModalBody>
        <ModalFooter>
          <Button style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 8, color: C.textMed }} onClick={() => setGenerateModal(false)}>Cancel</Button>
          <Button style={{ background: `linear-gradient(135deg, ${C.coral}, #00bfa5)`, border: "none", borderRadius: 8, fontWeight: 600 }} onClick={handleGenerate}>Generate</Button>
        </ModalFooter>
      </Modal>
    </Fragment>
  );
};

export default ApiKeyList;
